/**
 * Arquitetura de informação do painel — fonte única.
 *
 * Tudo que é menu, rota e título de aba sai DAQUI. O projeto anterior tinha
 * cinco páginas sem rota e itens de menu que levavam a lugar nenhum; a única
 * forma de isso não voltar é não existirem duas listas.
 *
 * Cada item declara:
 *  - `permission`: se o papel não tem, o item não aparece (esconder é UX; quem
 *    barra é o servidor);
 *  - `phase`: telas de fase futura existem como rota com EmptyState honesto.
 */
import {
  AlertTriangle,
  BarChart3,
  Contact,
  CreditCard,
  FileText,
  GraduationCap,
  History,
  LayoutDashboard,
  Megaphone,
  Radio,
  Settings,
  Shuffle,
  ShoppingBag,
  Target,
  Users,
} from 'lucide-react';
import type { BottomNavItem, NavGroup, NavItem } from '@mgdlead/ui';
import { can } from '@mgdlead/shared';
import type { Permission, WorkspaceRole } from '@mgdlead/shared';

export interface PhaseInfo {
  /** Número da fase em docs/product/plano-de-fases.md. */
  number: number;
  /** O que a fase entrega, em uma frase. */
  delivers: string;
}

export interface WorkspaceNavItem {
  id: string;
  label: string;
  /** Caminho relativo a `/w/:slug`. String vazia = a própria raiz. */
  path: string;
  icon: NavItem['icon'];
  permission?: Permission;
  /** Ausente = a tela já existe de verdade. */
  phase?: PhaseInfo;
  /** Descrição usada no EmptyState e no título da página. */
  summary: string;
}

export interface WorkspaceNavGroup {
  id: string;
  label?: string;
  items: WorkspaceNavItem[];
}

export const NAVIGATION: readonly WorkspaceNavGroup[] = [
  {
    id: 'principal',
    items: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        path: '',
        icon: LayoutDashboard,
        permission: 'analytics:read',
        summary: 'Os números do dia: entradas, saídas, saldo e custo por entrada.',
      },
    ],
  },
  {
    id: 'aquisicao',
    label: 'Aquisição',
    items: [
      {
        id: 'campanhas',
        label: 'Campanhas',
        path: 'campanhas',
        icon: Megaphone,
        permission: 'campaign:read',
        // Fase 4 ENTREGUE: a tela de campanhas existe de verdade
        // (`pages/workspace/campaigns`). Sem `phase`, `routes.tsx` para de gerar a tela
        // de "fase futura" para este item e a rota real assume o lugar.
        summary: 'Agrupa grupos, redirecionadores e anúncios sob um mesmo código.',
      },
      {
        id: 'redirecionadores',
        label: 'Redirecionadores',
        path: 'redirecionadores',
        icon: Shuffle,
        permission: 'redirector:read',
        // Fase 5 ENTREGUE: a tela existe de verdade (`pages/workspace/redirectors`).
        // Sem `phase`, `routes.tsx` para de gerar a tela de "fase futura" para este item e
        // a rota real assume o lugar — sem o menu mudar de posição.
        summary: 'O link curto que distribui o lead entre os grupos com vaga.',
      },
      {
        id: 'meta-ads',
        label: 'Meta Ads',
        path: 'meta-ads',
        icon: Target,
        permission: 'integration:read',
        // Fase 8 ENTREGUE: a tela existe de verdade (`pages/workspace/ads`). Sem `phase`,
        // `routes.tsx` para de gerar a tela de "fase futura" para este item e a rota real
        // assume o lugar — sem o menu mudar de posição.
        summary: 'Conta de anúncios conectada e custo diário por conjunto.',
      },
      {
        id: 'shopee',
        label: 'Shopee',
        path: 'shopee',
        icon: ShoppingBag,
        // Lucro por lead é número de painel: quem lê o Dashboard lê isto. Conectar é do OWNER
        // (`earnings:connect`), e a tela esconde o botão de quem não pode.
        permission: 'analytics:read',
        summary: 'Comissão de afiliado chegando sozinha e lucro por lead de cada bloco.',
      },
    ],
  },
  {
    id: 'whatsapp',
    label: 'WhatsApp',
    items: [
      {
        id: 'monitoradores',
        label: 'Monitoradores',
        path: 'monitoradores',
        icon: Radio,
        permission: 'monitor:read',
        // Fase 2 ENTREGUE: a tela existe de verdade (`pages/workspace/monitors`).
        // Sem `phase`, e por isso `routes.tsx` deixa de gerar a tela de "fase futura"
        // para este item — a rota real assume o lugar sem o menu mudar de posição.
        summary: 'Os números conectados que monitoram os grupos.',
      },
      {
        id: 'grupos',
        label: 'Grupos',
        path: 'grupos',
        icon: Users,
        permission: 'group:read',
        // Fase 3 EM ENTREGA: a tela de grupos existe de verdade
        // (`pages/workspace/groups`). Sem `phase`, `routes.tsx` para de gerar a tela
        // de "fase futura" para este item e a rota real assume o lugar.
        summary: 'Grupos importados e quanto ainda cabe em cada um.',
      },
      {
        id: 'contatos',
        label: 'Contatos',
        path: 'contatos',
        icon: Contact,
        permission: 'group:export_contacts',
        // 11/10: o download de contatos e o backup ganharam lugar próprio no menu — antes
        // ficavam num botão de Grupos e no detalhe de cada grupo, difíceis de achar.
        summary: 'Baixar listas de contatos e fazer o backup de todo mundo dos grupos.',
      },
    ],
  },
  {
    id: 'analytics',
    label: 'Analytics',
    items: [
      {
        id: 'analytics-gestao',
        label: 'Gestão',
        path: 'analytics/gestao',
        icon: BarChart3,
        permission: 'analytics:read',
        // Fase 6 ENTREGUE: a tela existe de verdade
        // (`pages/workspace/dashboard/GestaoPage.tsx`). Sem `phase`, `routes.tsx` para de
        // gerar a tela de "fase futura" para este item e a rota real assume o lugar.
        summary: 'Comparação entre campanhas, grupos e períodos.',
      },
      {
        id: 'analytics-historico',
        label: 'Histórico',
        path: 'analytics/historico',
        icon: History,
        permission: 'analytics:read',
        // Fase 6 ENTREGUE: `pages/workspace/dashboard/HistoricoPage.tsx`.
        summary: 'Hora do dia, atividade do ano e retenção por turma de entrada.',
      },
      {
        id: 'analytics-relatorios',
        label: 'Relatórios',
        path: 'analytics/relatorios',
        icon: FileText,
        permission: 'analytics:export',
        // Fase 9 ENTREGUE: a tela existe de verdade
        // (`pages/workspace/dashboard/RelatoriosPage.tsx`). Sem `phase`, `routes.tsx` para
        // de gerar a tela de "fase futura" para este item e a rota real assume o lugar —
        // sem o menu mudar de posição.
        summary: 'Exportação em CSV e PDF por campanha e período.',
      },
    ],
  },
  {
    id: 'operacao',
    label: 'Operação',
    items: [
      {
        id: 'incidentes',
        label: 'Incidentes',
        path: 'incidentes',
        icon: AlertTriangle,
        permission: 'incident:read',
        // Fase 7 ENTREGUE: a tela existe de verdade (`pages/workspace/incidents`).
        // Sem `phase`, `routes.tsx` para de gerar a tela de "fase futura" para este item e
        // a rota real assume o lugar — sem o menu mudar de posição.
        summary: 'Monitor fora do ar, grupo sem vaga, divergência de contagem.',
      },
    ],
  },
  {
    id: 'aprender',
    label: 'Aprender',
    items: [
      {
        id: 'treinamentos',
        label: 'Treinamentos',
        path: 'treinamentos',
        icon: GraduationCap,
        /**
         * Fase 11 ENTREGUE: a tela existe de verdade (`pages/workspace/training`). Sem
         * `phase`, `routes.tsx` para de gerar a tela de "fase futura" para este item e a
         * rota real assume o lugar — sem o menu mudar de posição.
         *
         * **Sem `permission` de propósito.** Treinamento é para todo mundo que entra no
         * painel, inclusive o `MEMBER` convidado ontem — que é justamente quem mais
         * precisa. Uma permissão aqui só serviria para esconder o material de quem
         * acabou de chegar.
         */
        summary: 'Cursos curtos sobre operação de grupos e tráfego.',
      },
    ],
  },
  {
    id: 'conta',
    label: 'Conta',
    items: [
      {
        id: 'assinatura',
        label: 'Assinatura',
        path: 'assinatura',
        icon: CreditCard,
        permission: 'billing:read',
        /**
         * Sem `phase`: a tela existe (Fase 10). Ficou fora daqui o HISTÓRICO DE FATURAS,
         * que só faz sentido quando houver cobrança — e não há gateway nesta entrega.
         * Prometer "faturas" no resumo de um menu que leva a uma tela sem fatura nenhuma
         * é rótulo de fase anterior mentindo na tela.
         */
        summary: 'Plano vigente, ciclo, vencimento e o consumo real de cada limite.',
      },
      {
        id: 'configuracoes',
        label: 'Configurações',
        path: 'configuracoes',
        icon: Settings,
        permission: 'workspace:read',
        summary: 'Perfil, workspace, aparência, segurança e integrações.',
      },
    ],
  },
];

/** Todos os itens, em ordem — usado para montar as rotas. */
export const NAV_ITEMS: readonly WorkspaceNavItem[] = NAVIGATION.flatMap((group) => group.items);

export function workspacePath(slug: string, path: string): string {
  return path.length === 0 ? `/w/${slug}` : `/w/${slug}/${path}`;
}

function visible(item: WorkspaceNavItem, role: WorkspaceRole): boolean {
  return item.permission === undefined || can(role, item.permission);
}

/**
 * Contadores ao lado do item de menu, por `id` (hoje só `incidentes`).
 *
 * O mapa é OPCIONAL e o valor é um objeto — nunca um número solto — justamente para que
 * "ainda não sei" (chave ausente) não possa ser confundido com "sei, e é zero". Quem
 * decide isso é `incidentBadge` em `features/incident/incident-ui.ts`: enquanto a contagem
 * não chega, o item não ganha badge nenhum. Um `0` no menu seria o painel jurando que está
 * tudo bem sem ter perguntado (invariante 8).
 */
export interface NavBadge {
  count: number;
  tone: 'danger' | 'warning' | 'neutral';
}

export type NavBadges = Readonly<Partial<Record<string, NavBadge>>>;

/** Menu lateral filtrado pelo papel. Grupo que ficou vazio não aparece. */
export function buildNavigation(
  slug: string,
  role: WorkspaceRole,
  badges: NavBadges = {},
): NavGroup[] {
  return NAVIGATION.map((group) => {
    const items: NavItem[] = group.items
      .filter((item) => visible(item, role))
      .map((item) => {
        const badge = badges[item.id];
        return {
          id: item.id,
          label: item.label,
          href: workspacePath(slug, item.path),
          icon: item.icon,
          ...(badge ? { badge: badge.count, badgeTone: badge.tone } : {}),
        };
      });

    return { id: group.id, ...(group.label ? { label: group.label } : {}), items };
  }).filter((group) => group.items.length > 0);
}

/**
 * Barra inferior do celular: no máximo 5, e são os destinos que o usuário
 * checa "em pé, no celular" (seção 11 do DESIGN_SYSTEM).
 */
const BOTTOM_NAV_IDS = ['dashboard', 'grupos', 'incidentes', 'monitoradores', 'campanhas'] as const;

export function buildBottomNav(
  slug: string,
  role: WorkspaceRole,
  badges: NavBadges = {},
): BottomNavItem[] {
  return BOTTOM_NAV_IDS.map((id) => NAV_ITEMS.find((item) => item.id === id))
    .filter((item): item is WorkspaceNavItem => item !== undefined && visible(item, role))
    .slice(0, 5)
    .map((item) => {
      const badge = badges[item.id];
      return {
        id: item.id,
        label: item.label,
        href: workspacePath(slug, item.path),
        icon: item.icon,
        // A barra de baixo não tem tom: o contador aqui é o mesmo número, sem a cor.
        ...(badge ? { badge: badge.count } : {}),
      };
    });
}

/**
 * Qual item está ativo para um caminho.
 * Casa pelo prefixo MAIS LONGO: `/analytics/gestão` não pode ativar o
 * Dashboard só porque este é prefixo de todo mundo.
 */
export function resolveActiveId(pathname: string, slug: string): string | undefined {
  const base = `/w/${slug}`;
  if (!pathname.startsWith(base)) return undefined;

  const rest = pathname.slice(base.length).replace(/^\/+/, '').replace(/\/+$/, '');
  if (rest.length === 0) return 'dashboard';

  let best: WorkspaceNavItem | undefined;
  for (const item of NAV_ITEMS) {
    if (item.path.length === 0) continue;
    if (rest === item.path || rest.startsWith(`${item.path}/`)) {
      if (best === undefined || item.path.length > best.path.length) best = item;
    }
  }
  return best?.id;
}

export function navItemById(id: string): WorkspaceNavItem | undefined {
  return NAV_ITEMS.find((item) => item.id === id);
}
